import React from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {
  Col,
  UiBox,
  UiButton,
  UiFormControl,
  UiFormErrorMessage,
  UiImage,
  UiQuestionnaireLayout,
  UiSimpleGrid,
  UiText,
  UiTextarea,
} from '~/components/elements';
import {UiCard} from '~/components/elements/UiCard';
import {ScrollToBottomButton} from '~/components/shared/ScrollToBottomButton';
import {BIRTH_CONTROL_REFILL_KEYS} from '~/constants/medical-questionnaires';
import {useInput, useNotification} from '~/hooks';
import {setNextQuestionnaire, setQuestionnaireAnswer} from '~/redux/questionnaires/slice';
import {Relation} from '~/redux/questionnaires/types';
import {RootState} from '~/redux/root-reducer';
import {AppDispatch} from '~/redux/root-store';
import {spacing} from '~/utils/theme/spacing';

const Questionnaire4bScreen = () => {
  const dispatch: AppDispatch = useDispatch();
  const {addMessage, addValidationMessage} = useNotification(dispatch);

  const currentAnswer = useSelector(
    (state: RootState) => state.questionnaires?.collectData[BIRTH_CONTROL_REFILL_KEYS.LAST_BC_PURCHASE]?.data,
  );

  const medicalQuestionnaire = useSelector((state: RootState) => state.questionnaires.medicalQuestionnaire);

  const questionnaires = medicalQuestionnaire?.questionnaires;
  const questionnaire = questionnaires?.find(i => i.key === BIRTH_CONTROL_REFILL_KEYS.LAST_BC_PURCHASE);

  const product = questionnaire?.questions?.product;

  const rules = {
    selectedNoHappy: (attributes: any) => {
      return {
        presence: {allowEmpty: false, message: `^This field can't be blank`},
        length: {
          minimum: 5,
          tooShort: '^This field is too short (minimum is %{count} characters)',
          tokenizer: function (value: any) {
            return trimSpaces(value);
          },
        },
      };
    },
  };
  const {inputs, setInputs, validation, trimSpaces} = useInput(
    {
      selectedOption: currentAnswer?.selectedOption || product?.id || '',
      selectedHappy: currentAnswer?.selectedHappy || '',
      selectedNoHappy: currentAnswer?.selectedNoHappy || '',
    },
    {rules},
  );

  const onClickSubmit = async () => {
    if (inputs.selectedHappy === 'NO') {
      const errors = validation.getErrors();
      if (errors) {
        return;
      }
    }
    dispatch(
      setQuestionnaireAnswer({
        key: BIRTH_CONTROL_REFILL_KEYS.LAST_BC_PURCHASE,
        index: questionnaire?.index || '',
        data: inputs,
      }),
    );
    dispatch(setNextQuestionnaire({value: BIRTH_CONTROL_REFILL_KEYS.STATEMENT_BEST_APPLIES}));
  };

  const renderHappyRelation = () => {
    if (inputs.selectedHappy === 'NO') {
      const selectedHappyData = questionnaire?.happyOptions?.find(i => i.value === inputs.selectedHappy);
      return (
        <UiBox w="100%" mt={spacing.small}>
          {selectedHappyData?.relations?.map(i => {
            if (i?.elementType?.name === 'inputArea') {
              return (
                <UiBox
                  w="100%"
                  mt={spacing.small}
                  key={i?.key || i?.elementType?.name}
                  className="animate__animated animate__fadeInLeft"
                >
                  <UiText fontSize="md" fontWeight="600" mt={spacing.medium}>
                    {i?.questions?.default}
                  </UiText>
                  <UiFormControl id="selectedNoHappy" isRequired isInvalid={!!validation?.errors?.selectedNoHappy}>
                    <UiTextarea
                      mt={spacing.small}
                      bg="white"
                      borderRadius={spacing.tiny}
                      placeholder={i?.elementType?.placeholder}
                      value={inputs.selectedNoHappy}
                      onChange={({target}) => setInputs({selectedNoHappy: target.value})}
                      maxH="100px"
                    />
                    {validation?.errors?.selectedNoHappy && (
                      <UiFormErrorMessage>{validation?.errors?.selectedNoHappy[0]}</UiFormErrorMessage>
                    )}
                  </UiFormControl>
                </UiBox>
              );
            }
            return null;
          })}
        </UiBox>
      );
    }
    return null;
  };

  return (
    <UiQuestionnaireLayout
      handleOnNext={onClickSubmit}
      checkoutStep={questionnaire?.checkoutStep}
      actions={questionnaire?.actions}
      disableNext={!inputs.selectedHappy}
    >
      <Col alignItems="center" textAlign="center">
        <UiImage type="icon" name={questionnaire?.icon} boxSize={spacing.mLarge} mt={spacing.medium} />

        <UiText fontSize="xl" fontWeight="bold" mt={spacing.large}>
          {questionnaire?.questions?.default}
        </UiText>
        <UiCard variant="rounded" boxShadow="base" borderRadius="lg" py={2} mt={spacing.small}>
          <UiText fontWeight="600">{product?.name}</UiText>
        </UiCard>
        <UiBox w="100%" mt={spacing.medium}>
          <UiText fontSize="md" fontWeight="bold">
            {questionnaire?.questions?.sub}
          </UiText>
          <UiSimpleGrid columns={2} spacing={5} mt={spacing.medium}>
            {questionnaire?.happyOptions?.map(value => (
              <ScrollToBottomButton
                key={`${value.label}-${value.value}`}
                title={value.label}
                variant={inputs.selectedHappy === value.value ? 'solid' : 'toggle'}
                onClick={() => setInputs({selectedHappy: value.value})}
              />
            ))}
          </UiSimpleGrid>
          {renderHappyRelation()}
        </UiBox>
      </Col>
    </UiQuestionnaireLayout>
  );
};

export default Questionnaire4bScreen;
